import { FormContext } from "@common/contexts/Form";
import FormField, { type FormFieldProps } from "@common/controls/Form/Field/component";
import { type ValidateFn } from "@common/controls/Form/Field/lib";
import { MoneyWithCurrencyInput, type MoneyWithCurrencyValue } from "@common/controls/Inputs/Number/Money";
import { useI18n } from "@common/hooks/i18n";
import { validateValueEx } from "@common/lib/validate";
import React from "react";

export type MoneyWithCurrencyFormFieldProps = Omit<
  FormFieldProps<string | number | undefined | null, any>,
  "Component" | "isNoStyle" | "isTrueRequired" | "name" | "value"
> & {
  amountName: string;
  amount: number | null | undefined;
  currency: string | null | undefined;
  currencyName: string;
  placeholder?: string;
  defaultCurrency?: string;
};

const MoneyWithCurrencyFormField = ({
  amount,
  amountName,
  currency,
  currencyName,
  placeholder,
  validator,
  ...props
}: MoneyWithCurrencyFormFieldProps) => {
  const i18n = useI18n();

  const context = React.useContext(FormContext);

  const onChange = React.useCallback(
    (name: string, value: MoneyWithCurrencyValue | null) => {
      const onChange = props.onChange ?? context.onChange!;

      onChange(currencyName, value?.currency ?? null);
      onChange(amountName, value?.amount ?? null);
    },
    [amountName, currencyName, props.onChange]
  );

  const validate = React.useCallback<ValidateFn>(
    async (i18n, v: MoneyWithCurrencyValue | null) => {
      if (props.isRequired && ((!v?.amount && v?.amount !== 0) || !v?.currency)) {
        return i18n.errorRequiredField;
      }

      if (validator)
        return await validateValueEx(i18n, v?.amount, {
          isRequired: false,
          isTrueRequired: false,
          options: validator
        });

      return null;
    },
    [i18n, validator, props.isRequired]
  );

  return (
    <FormField
      {...props}
      name={currencyName}
      Component={MoneyWithCurrencyInput}
      onChange={onChange}
      value={{
        currency: currency ?? props.defaultCurrency,
        amount
      }}
      validator={validate}
    />
  );
};
export default MoneyWithCurrencyFormField;
